import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LineChart, Pencil, Share, Undo2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GuesteriaPartnerEditRegionDialog } from "@/components/pages/guesteria-partners/components/GuesteriaPartnerEditRegionDialog";
import { GuesteriaPartnerRegionValue } from "@/components/pages/guesteria-partners/components/GuesteriaPartnerRegionValue";
import { GuesteriaPartnerLogo } from "@/components/pages/guesteria-partners/components/GuesteriaPartnerLogo";
import { GuesteriaPartnerRevenueChart } from "@/components/pages/guesteria-partners/components/GuesteriaPartnerRevenueChart";
import { usePartnerOrdersSummary } from "@/components/pages/guesteria-partners/hooks/usePartnerOrdersSummary";
import { downloadPartnerDetailExport } from "@/components/pages/guesteria-partners/utils/exportPartnerDetailAnalytics";
import { formatPartnerRevenueValue } from "@/components/pages/guesteria-partners/utils/guesteriaPartnersFormat";
import {
  mapPartnerSettingsToDetail,
  providerSlugFromSettings,
} from "@/components/pages/guesteria-partners/utils/mapPartnerSettingsToGuesteria";
import { usePartnerRevenueAnalytics } from "@/components/pages/guesteria-partners/hooks/usePartnerRevenueAnalytics";
import { usePartnerRegionFlag } from "@/components/pages/guesteria-partners/hooks/usePartnerRegionFlag";
import { useGetPartnerSettings } from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import { useHotel } from "@/hooks/useHotel";
import { BillingPageState } from "@/components/pages/billing-settings/components/BillingPageState";
import type { PartnerRevenueAnalyticsPeriod } from "@/types/partner-revenue-analytics";
import type { GuesteriaPartnerRevenueChartPoint } from "@/components/pages/guesteria-partners/components/GuesteriaPartnerRevenueChart";
import { cn } from "@/lib/utils";

function PartnerAnalyticsChartBody({
  isLoading,
  isError,
  chartPoints,
  currency,
  loadingText,
  errorText,
  emptyText,
}: {
  isLoading: boolean;
  isError: boolean;
  chartPoints: GuesteriaPartnerRevenueChartPoint[];
  currency: string;
  loadingText: string;
  errorText: string;
  emptyText: string;
}): React.JSX.Element {
  if (isLoading) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        {loadingText}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-destructive py-8 text-center text-sm">{errorText}</p>
    );
  }

  if (chartPoints.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        {emptyText}
      </p>
    );
  }

  return (
    <GuesteriaPartnerRevenueChart data={chartPoints} currency={currency} />
  );
}

export const GuesteriaPartnerDetailPage = (): React.JSX.Element | null => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerId } = useParams<{ partnerId: string }>();
  const { hotelId } = useHotel();
  const { data: settings, isLoading, isError } = useGetPartnerSettings();
  const { data: regionFlagEmoji } = usePartnerRegionFlag(
    hotelId,
    Boolean(
      settings?.destinationName?.trim() && !settings?.destination?.flagEmoji,
    ),
  );

  const partner = useMemo(() => {
    if (!settings || !partnerId) {
      return undefined;
    }
    if (providerSlugFromSettings(String(settings.provider)) !== partnerId) {
      return undefined;
    }
    return mapPartnerSettingsToDetail(settings, t, regionFlagEmoji);
  }, [settings, partnerId, t, regionFlagEmoji]);

  const [analyticsPeriod, setAnalyticsPeriod] =
    useState<PartnerRevenueAnalyticsPeriod>("year");

  const analyticsQueryEnabled = Boolean(partner && settings && hotelId);
  const provider = settings?.provider;

  const {
    chartPoints,
    currency: analyticsCurrency,
    isLoading: isAnalyticsLoading,
    isError: isAnalyticsError,
  } = usePartnerRevenueAnalytics(
    analyticsPeriod,
    provider,
    analyticsQueryEnabled,
  );

  const {
    data: ordersSummary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = usePartnerOrdersSummary(analyticsPeriod, provider, analyticsQueryEnabled);

  const summaryCurrency = ordersSummary?.currency ?? analyticsCurrency;
  const isAnalyticsDataLoading = isAnalyticsLoading || isSummaryLoading;
  const isAnalyticsDataError = isAnalyticsError || isSummaryError;
  const isExportDisabled = isAnalyticsDataLoading;

  const handleExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (!partner || !hotelId || isExportDisabled) {
        return;
      }

      const emptyPh = t("partners.detail.emptyPlaceholder");
      const periodLabelMap: Record<PartnerRevenueAnalyticsPeriod, string> = {
        year: t("partners.detail.periodYear"),
        sixMonths: t("partners.detail.periodSixMonths"),
        month: t("partners.detail.periodMonth"),
        week: t("partners.detail.periodWeek"),
      };

      try {
        downloadPartnerDetailExport(format, {
          partner,
          hotelId,
          period: analyticsPeriod,
          periodLabel: periodLabelMap[analyticsPeriod],
          connectionStatusLabel:
            partner.connectionStatus === "installed"
              ? t("partners.table.connectionStatusValues.installed")
              : t("partners.table.connectionStatusValues.unavailable"),
          categoryText:
            partner.categoryLabels.length > 0
              ? partner.categoryLabels.join(", ")
              : emptyPh,
          regionText: partner.region?.city?.trim()
            ? [partner.region.flagEmoji, partner.region.city]
                .filter(Boolean)
                .join(" ")
            : emptyPh,
          revenueSummary:
            ordersSummary != null
              ? formatPartnerRevenueValue(
                  ordersSummary.totalRevenue,
                  summaryCurrency,
                )
              : emptyPh,
          commissionText:
            partner.commissionPercent != null
              ? `${partner.commissionPercent}%`
              : emptyPh,
          currency: summaryCurrency,
          points: chartPoints,
        });
        toast.success(t("partners.detail.exportSuccess"));
      } catch {
        toast.error(t("partners.detail.exportError"));
      }
    },
    [
      analyticsPeriod,
      chartPoints,
      hotelId,
      isExportDisabled,
      ordersSummary,
      partner,
      summaryCurrency,
      t,
    ],
  );

  if (!partnerId) {
    return null;
  }

  if (!hotelId) {
    return <BillingPageState text={t("partners.hotelNotSelected")} />;
  }

  if (isLoading) {
    return <BillingPageState text={t("partners.loading")} />;
  }

  if (isError) {
    return <BillingPageState text={t("partners.loadingError")} tone="error" />;
  }

  if (!partner || !settings) {
    return (
      <div className="flex w-full flex-col gap-8 p-6">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate("/guesteria-partners")}
          className="h-8 w-fit justify-start self-start rounded-md bg-transparent px-3 py-1 text-base font-normal text-black hover:bg-transparent"
        >
          <Undo2 className="h-6 w-6" />
          {t("partners.detail.back")}
        </Button>
        <p className="text-muted-foreground">{t("partners.detail.notFound")}</p>
      </div>
    );
  }

  const emptyPh = t("partners.detail.emptyPlaceholder");

  let revenueDisplayText = emptyPh;
  if (ordersSummary != null) {
    revenueDisplayText = formatPartnerRevenueValue(
      ordersSummary.totalRevenue,
      summaryCurrency,
    );
  } else if (isSummaryLoading) {
    revenueDisplayText = t("partners.detail.analyticsLoading");
  }

  const categoryText =
    partner.categoryLabels.length > 0
      ? partner.categoryLabels.join(", ")
      : emptyPh;

  return (
    <div className="flex w-full min-w-0 flex-col gap-6 p-6">
      <Button
        type="button"
        variant="ghost"
        onClick={() => navigate("/guesteria-partners")}
        className="h-8 w-fit justify-start self-start rounded-md bg-transparent px-3 py-1 text-base font-normal text-black hover:bg-transparent"
      >
        <Undo2 className="h-6 w-6" />
        {t("partners.detail.back")}
      </Button>

      <div className="flex flex-col items-center gap-2">
        <GuesteriaPartnerLogo
          logo={partner.logo}
          variant="detail"
          alt={partner.name}
        />
        <h1 className="text-foreground text-center text-lg leading-none font-medium">
          {partner.name}
        </h1>
      </div>

      <div className="flex w-full flex-col gap-6 text-sm">
        <section className="flex min-h-6 flex-row flex-wrap items-center gap-2 px-0">
          <span className="text-foreground shrink-0 text-sm leading-none font-semibold">
            {t("partners.detail.connectionStatus")}
          </span>
          {partner.connectionStatus === "installed" ? (
            <span className="inline-flex min-h-6 shrink-0 items-center justify-center rounded-md border-[0.83px] border-[#49A432]/50 bg-[#49A432]/5 px-2 py-1 text-xs leading-none font-normal text-[#49A432]">
              {t("partners.table.connectionStatusValues.installed")}
            </span>
          ) : (
            <span className="inline-flex min-h-6 shrink-0 items-center justify-center rounded-md border-[0.83px] border-[#DFAC4F]/50 bg-[#DFAC4F]/5 px-2 py-1 text-xs leading-none font-normal text-[#DFAC4F]">
              {t("partners.table.connectionStatusValues.unavailable")}
            </span>
          )}
        </section>

        <section className="-mt-2 flex flex-col gap-4">
          <span className="text-foreground text-sm leading-normal font-medium">
            {t("partners.detail.description")}
          </span>
          <p className="text-muted-foreground text-sm leading-normal font-normal">
            {partner.description}
          </p>
        </section>

        <section className="flex min-h-6 flex-row flex-wrap items-center gap-2 px-0">
          <span className="text-foreground shrink-0 text-sm leading-normal font-medium">
            {t("partners.detail.category")}
          </span>
          <span className="text-muted-foreground text-sm leading-normal font-normal">
            {categoryText}
          </span>
        </section>

        <section className="flex min-h-6 flex-row flex-wrap items-center gap-2 px-0">
          <span className="text-foreground shrink-0 text-sm leading-normal font-medium">
            {t("partners.detail.region")}
          </span>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {partner.region?.city?.trim() ? (
              <GuesteriaPartnerRegionValue
                city={partner.region.city}
                flagEmoji={partner.region.flagEmoji}
              />
            ) : (
              <span className="text-muted-foreground min-w-0 text-sm leading-normal font-normal">
                {emptyPh}
              </span>
            )}
            <GuesteriaPartnerEditRegionDialog
              settings={settings}
              trigger={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground h-8 w-8 shrink-0"
                  aria-label={t("partners.detail.editRegionAria")}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              }
            />
          </div>
        </section>

        <section className="flex min-h-6 flex-row flex-wrap items-center gap-2 px-0">
          <span className="text-foreground shrink-0 text-sm leading-normal font-medium">
            {t("partners.detail.commission")}
          </span>
          <span className="text-muted-foreground text-sm leading-normal font-normal">
            {partner.commissionPercent != null
              ? `${partner.commissionPercent}%`
              : emptyPh}
          </span>
        </section>

        <section className="flex min-h-6 flex-row flex-wrap items-center gap-2 px-0">
          <span className="text-foreground shrink-0 text-sm leading-normal font-medium">
            {t("partners.detail.revenue")}
          </span>
          <span className="text-muted-foreground text-sm leading-normal font-normal">
            {revenueDisplayText}
          </span>
        </section>
      </div>

      <Card className="w-full gap-6 py-6">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4 border-b pb-4">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <LineChart
                className="text-muted-foreground h-4 w-4"
                aria-hidden
              />
              {t("partners.detail.analytics")}
            </CardTitle>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={analyticsPeriod}
              onValueChange={(value) =>
                setAnalyticsPeriod(value as PartnerRevenueAnalyticsPeriod)
              }
            >
              <SelectTrigger size="sm" className="h-8 w-[120px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="min-w-[120px]">
                <SelectItem value="year">
                  {t("partners.detail.periodYear")}
                </SelectItem>
                <SelectItem value="sixMonths">
                  {t("partners.detail.periodSixMonths")}
                </SelectItem>
                <SelectItem value="month">
                  {t("partners.detail.periodMonth")}
                </SelectItem>
                <SelectItem value="week">
                  {t("partners.detail.periodWeek")}
                </SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="span"
              onClick={() => handleExport("xlsx")}
              disabled={isExportDisabled}
              className={cn(
                "border-input bg-background inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium shadow-xs",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <Share className="size-3.5 shrink-0" />
              {t("partners.detail.export")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <PartnerAnalyticsChartBody
            isLoading={isAnalyticsDataLoading}
            isError={isAnalyticsDataError}
            chartPoints={chartPoints}
            currency={analyticsCurrency}
            loadingText={t("partners.detail.analyticsLoading")}
            errorText={t("partners.detail.analyticsError")}
            emptyText={t("partners.detail.analyticsEmpty")}
          />
        </CardContent>
      </Card>
    </div>
  );
};
